import { Box, TextField, Typography } from '@mui/material';
import DatePicker from 'components/DatePicker';
import useLocalDispatch from 'hooks/useLocalDispatch';
import { useState } from 'react';
import { StyledForm } from 'styles';
import { CleanButton } from 'components/styled';
import styled from 'styled-components';
import { Alert, AlertTitle } from '@mui/material';
import { DataType } from '@constants';
import keyBy from 'lodash/keyBy';
import { Item } from 'types';
import { fetchDataToIntegrationServer } from 'thunk';
import { SingleValuePickerV2 } from 'components/SingleValuePicker';
import { calculateAlertSeverityByStatusCode } from 'utils';
import JsonViewer from 'shared/JsonViewer';
import { useFetchDataToIntegrationServerFromAgentWarning } from 'ReportsModule/Developer/hooks/useFetchDataToIntegrationServerFromAgentWarning';
import { useNotification } from 'hooks/useNotification';
import { useTenantRealmDropdownsFlow } from 'TenantModule/hooks/useTenantRealmDropdownsFlow';
import { TenantSelector } from 'TenantModule/components/TenantSelector';
import RealmSelector from 'SandboxModule/components/RealmSelector';
import ApiClientManager from 'core/apiClient';
import { useRef, MouseEventHandler } from 'react';

const stateId = 'fetch-data-to-integration-server-from-agent-card';

const StyledWarningText = styled(Alert)`
  margin: 10px 0px;
`;

const integrationDataTypes = [
  {
    label: 'All other importable data types (excluding Time Entry, Work Plan, Task)',
    value: 'all',
    WarningLabel: (
      <StyledWarningText severity="error">
        There is a 4 hour fetch limit for fetch all data. Please contact Integrations team if the
        data is still not showing up after you triggered a fetch.
      </StyledWarningText>
    ),
  },
  { label: 'Time Entry', value: DataType.TimeEntry },
  { label: 'Work Plan', value: DataType.WorkPlan },
  { label: 'Task', value: DataType.Task },
];

const integrationDataTypesArray = integrationDataTypes.map((item) => item.value);

const integrationDataTypesHash = keyBy(integrationDataTypes, 'value');
const defaultDataType = integrationDataTypesHash[DataType.TimeEntry];

export const FetchDataToIntegrationServerFromAgentForm: React.FC<{
  shouldShowWarningBeforeSubmission: boolean;
}> = ({ shouldShowWarningBeforeSubmission }) => {
  const { dispatchLocal, selectLocalData } = useLocalDispatch();
  const {
    isRequesting,
    data: stateData,
    error,
  } = selectLocalData<{
    status: number;
    data: string;
  }>({
    stateId,
  });
  const { openFetchDataToIntegrationServerFromAgentWarning } =
    useFetchDataToIntegrationServerFromAgentWarning();
  const { showSuccess, showError } = useNotification();

  // Tenant/realm selection using unified dropdowns (aligned with Tenant & Realm Management)
  const { selectedTenant, selectedRealm, onSelectTenantRealm, onSelectRealm } =
    useTenantRealmDropdownsFlow();
  const realmDropdownRef = useRef<HTMLDivElement>(null);
  const onTenantSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (e.currentTarget && realmDropdownRef.current) {
      onSelectTenantRealm({
        tenantDropdownTarget: e.currentTarget,
        realmDropdownTarget: realmDropdownRef.current,
      });
    }
  };
  const onRealmSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (selectedTenant) {
      onSelectRealm({ target: e.currentTarget, tenantId: selectedTenant.id });
    }
  };

  const [fetchDataFromAgentParams, setFetchDataFromAgentParams] = useState<{
    dataType: Item;
    targetServiceId: string;
    dateAfter: string;
    // dateBefore:string;
  }>({
    dataType: defaultDataType,
    targetServiceId: '',
    dateAfter: '',
    // dateBefore:"",
  });
  const isSelectingAllDataTypes =
    fetchDataFromAgentParams.dataType.value === integrationDataTypesHash['all'].value;
  const dataTypeHelperText = isSelectingAllDataTypes
    ? 'All importable data types on the Integration Settings page: employees, clients, projects, activities, etc.'
    : '';

  const onFetchDataToISFromAgentValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    if (name === 'dataType') {
      setFetchDataFromAgentParams({
        ...fetchDataFromAgentParams,
        [name as string]: integrationDataTypesHash[value],
      });
    } else {
      setFetchDataFromAgentParams({
        ...fetchDataFromAgentParams,
        [name]: value,
      });
    }
  };

  const onFetchDataConfirm = () => {
    const { dataType, targetServiceId, dateAfter } = fetchDataFromAgentParams;
    if (targetServiceId.trim()) {
      const params = {
        targetServiceId: Number(targetServiceId),
        dataType: dataType.value as string,
        fetchFrom: 'agent',
        dateAfter: dateAfter ? dateAfter : undefined,
        tenantName: selectedTenant?.tenantName || undefined,
        realmId: selectedRealm?.realm_id || undefined,
      };
      dispatchLocal(fetchDataToIntegrationServer, {
        params,
        stateId,
        formatSuccessResponse: ({ response }) => {
          return {
            status: response.status,
            data: response.data,
          };
        },
        formatFailureResponse: ({ error }: { error: any }) => {
          return {
            status: error.status,
            data: error.data,
          };
        },
        onSuccessCallback: ({ response }) => {
          const error = response.data?.errors?.[0];
          if (error) {
            showError(`Failed to trigger fetch data. ${error}`);
          } else {
            showSuccess('Successfully triggered fetch data.');
          }
        },
      });
    }
  };

  const handleSubmit: React.MouseEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    // The MUI X picker's hidden input holds placeholder text for a partial
    // date, so the native `required` constraint no longer blocks submission
    // the way the previous <input type="date"> did. An empty dateAfter would
    // dispatch an unbounded fetch of all historical data.
    if (!fetchDataFromAgentParams.dateAfter) {
      showError('Start Date is required.');
      return;
    }

    if (shouldShowWarningBeforeSubmission) {
      openFetchDataToIntegrationServerFromAgentWarning({
        onConfirm: onFetchDataConfirm,
      });
    } else {
      onFetchDataConfirm();
    }
  };

  const dataTypeValue = String(fetchDataFromAgentParams.dataType.value);

  const { WarningLabel } = integrationDataTypesHash[dataTypeValue];

  return (
    <>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '1.125rem', mb: 1 }}>
        Fetch Data to Integration Server
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 3,
        }}
      >
        From Agent
      </Typography>

      <StyledForm onSubmit={handleSubmit}>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Organization
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              {selectedTenant ? (
                <TenantSelector onClick={onTenantSelectorClick} tenantId={selectedTenant.id} />
              ) : (
                <TenantSelector
                  onClick={onTenantSelectorClick}
                  tenantId={ApiClientManager.getTenantId()}
                />
              )}
              <RealmSelector
                onClick={onRealmSelectorClick}
                realmId={selectedRealm?.realm_id || ''}
                ref={realmDropdownRef}
              />
            </Box>
          </Box>
          <SingleValuePickerV2
            items={integrationDataTypesArray}
            item={dataTypeValue}
            itemsHash={integrationDataTypesHash}
            inputLabel="Data Type"
            inputName="dataType"
            onValueChange={onFetchDataToISFromAgentValueChange}
            helperText={dataTypeHelperText}
          />
          {WarningLabel}
          <TextField
            label="Integration ID"
            type="number"
            name="targetServiceId"
            value={fetchDataFromAgentParams.targetServiceId ?? ''}
            onChange={onFetchDataToISFromAgentValueChange}
            required
            fullWidth
            size="small"
          />
          <DatePicker
            label="Start Date"
            value={fetchDataFromAgentParams.dateAfter}
            onChange={(newDate) =>
              setFetchDataFromAgentParams((prev) => ({ ...prev, dateAfter: newDate ?? '' }))
            }
            required
            fullWidth
            size="small"
          />
          {/* <TextField
          label="End Date"
          type="date"
          
          name="dateBefore"
          value={fetchDataFromAgentParams.dateBefore}
          onChange={onFetchDataToISFromAgentValueChange}
          InputLabelProps={{
            shrink: true,
          }}
        /> */}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 2,
              mt: 'auto',
            }}
          >
            {!isRequesting && stateData && (
              <Alert severity={calculateAlertSeverityByStatusCode(stateData.status)}>
                <AlertTitle>{stateData.status}</AlertTitle>
                <JsonViewer collapsed src={{ response: stateData?.data || '' }} />
              </Alert>
            )}
            <CleanButton variant="contained" type="submit" disabled={isRequesting} fullWidth>
              Request
            </CleanButton>
          </Box>
        </Box>
      </StyledForm>
    </>
  );
};
